/**
 * BlinkSkillEffect — Specter wraith-path Evasion active: long-range stealth dash.
 *
 * A 3-tile repositioning dash that passes through enemies with full i-frames.
 * Consumes 1 dash charge. Doesn't break Stealth (uses teleport-style
 * reposition, same as Wraith/Flicker). Medium cooldown (1200ms). Fizzle at
 * low XP.
 *
 * I-frames are granted for a brief window after the blink — the reposition
 * itself is instant (teleport), but the player is invulnerable for a
 * configurable duration afterward to sell the "phasing" fantasy.
 *
 * Upgrade hooks wired through resolver scope 'wraith':
 * - Quickstep (cooldownReduction): fraction reduction on cooldown
 * - Afterimage (afterimageDurationMs, afterimageDrawsInvestigation):
 *   spawns investigation decoy at origin on activation
 *
 * Enhanced by:
 * - Ghost Dash: dashing while concealed no longer generates detection events
 *   (handled at DashComponent level, not queried here)
 * - Phase Dash: Blink passes through thin walls
 *   (handled by movement/collision layer, not queried here)
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Blink
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BlinkDeps {
  readonly targeting: {
    shouldFizzle(): boolean;
    /** Current player facing direction (normalized). */
    getFacingDirection(): { x: number; y: number };
  };
  readonly dash: {
    /** Current available dash charges. */
    readonly charges: number;
    /** Consume one dash charge without starting a physics dash. */
    consumeCharge(): void;
    /** I-frame toggle — set true during blink window, cleared on expiry. */
    hasIFrames: boolean;
  };
  readonly movement: {
    /**
     * Reposition the player by an offset in world pixels.
     * This is a teleport-style move — it does not trigger movement-break
     * checks for Hide/Stealth.
     */
    reposition(dx: number, dy: number): void;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly afterimage?: {
    /** Spawn an investigation decoy at the given world position. */
    spawnDecoy(x: number, y: number, durationMs: number): void;
    /** Current player world position (for decoy spawn at origin). */
    getCurrentPosition(): { x: number; y: number };
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BlinkConfig {
  readonly cooldownMs: number;
  readonly rangePx: number;
  readonly iFrameDurationMs: number;
}

const DEFAULT_CONFIG: BlinkConfig = {
  cooldownMs: 1200,              // medium cooldown tier
  rangePx: 96,                   // 3 tiles at 32px/tile
  iFrameDurationMs: 150,         // brief invulnerability window after teleport
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BlinkSkillEffect implements SkillEffect {
  readonly skillId = 'blink';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: BlinkDeps;
  private readonly config: BlinkConfig;

  private _cooldownRemaining = 0;
  private _iFrameRemaining = 0;
  private _fizzled = false;
  private _activationCount = 0;
  private _resolvedCooldownMs = 0;

  constructor(deps: BlinkDeps, config: Partial<BlinkConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get fizzled(): boolean { return this._fizzled; }
  get activationCount(): number { return this._activationCount; }
  /** Whether i-frames are currently active from a recent blink. */
  get iFramesActive(): boolean { return this._iFrameRemaining > 0; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && this.deps.dash.charges > 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve cooldown: base minus Quickstep reduction (scope: 'wraith')
    const cooldownReduction = this.deps.resolver.getAdditiveEffect(
      'wraith', 'cooldownReduction',
    );
    this._resolvedCooldownMs = this.config.cooldownMs * (1 - cooldownReduction);

    // Fizzle check — consumes cooldown + charge but does not reposition
    this._fizzled = this.deps.targeting.shouldFizzle();

    if (this._fizzled) {
      this._cooldownRemaining = this._resolvedCooldownMs;
      this.deps.dash.consumeCharge();
      return false;
    }

    // Resolve Afterimage: spawn investigation decoy at origin before moving
    const hasAfterimage = this.deps.resolver.hasEffect('wraith', 'afterimageDrawsInvestigation');
    if (hasAfterimage && this.deps.afterimage) {
      const afterimageDuration = this.deps.resolver.getAdditiveEffect(
        'wraith', 'afterimageDurationMs',
      );
      const origin = this.deps.afterimage.getCurrentPosition();
      this.deps.afterimage.spawnDecoy(origin.x, origin.y, afterimageDuration);
    }

    // Consume dash charge and reposition (3 tiles, passes through enemies)
    this.deps.dash.consumeCharge();
    const dir = this.deps.targeting.getFacingDirection();
    this.deps.movement.reposition(
      dir.x * this.config.rangePx,
      dir.y * this.config.rangePx,
    );

    // Grant i-frames for the blink window
    this.deps.dash.hasIFrames = true;
    this._iFrameRemaining = this.config.iFrameDurationMs;

    // Start cooldown
    this._cooldownRemaining = this._resolvedCooldownMs;
    this._activationCount++;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick i-frame window
    if (this._iFrameRemaining > 0) {
      this._iFrameRemaining = Math.max(0, this._iFrameRemaining - delta);
      if (this._iFrameRemaining <= 0) {
        this.deps.dash.hasIFrames = false;
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    // Clean up i-frames if active
    if (this._iFrameRemaining > 0) {
      this._iFrameRemaining = 0;
      this.deps.dash.hasIFrames = false;
    }
  }
}
